import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  OrganizationType,
  type PersonSportOrganizationResponse,
} from '@on3/api';
import clsx from 'clsx';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import Link from 'next/link';
import { type MouseEventHandler, useCallback, useState } from 'react';
import { type IPlayerProfile } from 'utils/player';

import styles from './PlayerProfileTeamsList.module.scss';

interface IPlayerProfileTeamsListProps {
  playerProfileData: IPlayerProfile;
  className?: string;
  condensed?: boolean;
}

interface IPlayerProfileTeamProps {
  currentPlayerProfile: PersonSportOrganizationResponse;
  className?: string;
}

const PlayerProfileTeam = ({
  currentPlayerProfile,
  className,
}: IPlayerProfileTeamProps) => {
  const year = currentPlayerProfile?.expMax;
  const currentTeam = currentPlayerProfile?.organization;
  const name = currentTeam?.fullName;
  const mascot = currentTeam?.mascot;
  const slug = currentTeam?.slug;
  const assetUrl = currentTeam?.assetUrl;
  const mascotIndex = name?.indexOf(mascot || '') || -1;
  const displayName =
    mascotIndex === -1 ? name : name?.replace(`${mascot}`, '');
  const isHighSchool = currentTeam?.orgType === OrganizationType.HighSchool;

  return (
    <div className={clsx(className, styles.tile)}>
      <span>{year}</span>
      <div className={styles.team}>
        {assetUrl && (
          <TeamAvatar height={24} slug={slug} src={assetUrl} width={24} />
        )}
        <div className={styles.name}>
          {isHighSchool ? (
            <Link href={`/high-school/${currentTeam?.urlSlug}/`}>
              {displayName}
            </Link>
          ) : (
            <span>{displayName}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export const PlayerProfileTeamsList = ({
  playerProfileData,
  className,
  condensed,
}: IPlayerProfileTeamsListProps) => {
  const [currentProfile, setCurrentProfile] = useState(playerProfileData);

  const {
    organizations: allPlayerProfiles,
    currentIndex = 0,
    nextTeamKey,
    prevTeamKey,
  } = currentProfile || {};

  const currentTeamIndex = allPlayerProfiles?.findIndex(
    (profile) =>
      profile.organization?.key ===
      allPlayerProfiles[currentIndex].organization?.key,
  );

  const showArrows =
    allPlayerProfiles && allPlayerProfiles?.length > 1 && condensed;

  const handleClick: MouseEventHandler = useCallback(
    (e) => {
      const {
        currentTarget: { id },
      } = e;
      const indexDelta = parseInt(id, 10);

      if (!allPlayerProfiles) {
        return null;
      }

      setCurrentProfile((prevState) => {
        const newIndex = prevState?.currentIndex ?? currentIndex;
        const newCurrentIndex = indexDelta + newIndex;
        /**
         * updated state to display team name
         *
         * if we do want to go back to updating the entire profile,
         * just need to update `currentPlayerTeam` to  `organizations[newCurrentIndex]`
         * and change `currentIndex: newCurrentIndex` to `index: newCurrentIndex`
         */
        const newState = {
          ...prevState,
          currentIndex: newCurrentIndex,
          nextTeamKey:
            allPlayerProfiles[newCurrentIndex + 1]?.organization?.key,
          prevTeamKey:
            allPlayerProfiles[newCurrentIndex - 1]?.organization?.key,
        };

        return newState;
      });
    },
    [currentIndex, allPlayerProfiles],
  );

  return (
    <div
      className={clsx(className, styles.block, {
        [styles.condensed]: condensed,
      })}
    >
      {allPlayerProfiles?.map((profile, i) => (
        <PlayerProfileTeam
          className={clsx(i === currentTeamIndex && styles.currentTeam)}
          currentPlayerProfile={profile}
          key={profile.organization?.key}
        />
      ))}
      {showArrows && (
        <div className={styles.arrows}>
          <button disabled={!nextTeamKey} id="1" onClick={handleClick}>
            <KeyboardArrowLeft />
          </button>
          <button disabled={!prevTeamKey} id="-1" onClick={handleClick}>
            <KeyboardArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};
